import { I18nService } from '@/services/i18n-service';
import { SaveToProjectApi } from '@eureka/shared/save-to-project/services';
import type { ProjectListPublicResponse, Project, ProjectFolder, ProjectListResponse } from '@eureka/shared/save-to-project/services';
import { Message } from '@patsnap/tahiti-core';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { SuggestService } from './suggest-service';

export interface ProjectService {
    projectList: ProjectFolder[]
    loading: boolean
    loadingMore: boolean
    total: number
    currentPage: number
    pageSize: number
    hasMore: boolean
    create(project: ProjectFolder): Promise<ProjectFolder>
    update(project: ProjectFolder): Promise<ProjectFolder>
    remove(projectId: string): Promise<boolean>
    getList(): Promise<ProjectListPublicResponse[]>
    getListWithPagination(page: number): Promise<ProjectListResponse>
    loadMore(): Promise<void>
    updateProjectShareCount(projectId: string, increment?: number): void
}

export const ProjectService = Symbol('ProjectService');

@injectable()
export class ProjectServiceImpl implements ProjectService {
    private _projectList: ProjectFolder[] = []
    private _loading = false
    private _loadingMore = false
    private _total = 0
    private _currentPage = 0
    private _pageSize = 20
    private _hasMore = true

    constructor(
        @inject(SaveToProjectApi) private readonly _projectApi: SaveToProjectApi,
        @inject(I18nService) private readonly _i18nService: I18nService,
        @inject(Message) private readonly _message: Message,
        @inject(SuggestService) private readonly _suggestService: SuggestService
    ) {}

    get projectList() {
        return this._projectList;
    }

    get loading() {
        return this._loading;
    }

    get loadingMore() {
        return this._loadingMore;
    }

    get total() {
        return this._total;
    }

    get currentPage() {
        return this._currentPage;
    }

    get pageSize() {
        return this._pageSize;
    }

    set pageSize(value: number) {
        this._pageSize = value;
    }

    get hasMore() {
        return this._hasMore;
    }

    create(project: Project): Promise<ProjectFolder> {
        return this._projectApi.createFolder(project).then((project) => {
            return project
        })
    }

    update(project: ProjectFolder): Promise<ProjectFolder> {
        return this._projectApi.editFolder(project).then((project) => {
            this._projectList = this._projectList.map(p => p.work_project_id === project.work_project_id ? project : p)
            this._message.success({
                message: this._i18nService.t('workProject.updateSuccess'),
            })
            return project
        })
    }

    remove(projectId: string): Promise<boolean> {
        return this._projectApi.removeFolder(projectId).then(() => {
            this._projectList = this._projectList.filter(p => p.work_project_id !== projectId)
            this._message.success({
                message: this._i18nService.t('workProject.removeSuccess'),
            })
            if (this._projectList.length === 0) {
                this._suggestService.getHistoryItems()
            }
            return true
        })
    }

    getList(): Promise<ProjectListPublicResponse[]> {
        return this._projectApi.getFolderList()
    }

    getListWithPagination(page: number): Promise<ProjectListResponse> {
        // 只在首次加载或重置时设置loading，加载更多时不设置
        const isInitialLoad = page === 1;
        if (isInitialLoad) {
            this._loading = true;
        } else {
            this._loadingMore = true;
        }

        return this._projectApi.getFolderListWithPagination({
            page,
            limit: this._pageSize,
        }).then((response) => {
            this._total = response.total
            this._currentPage = page
            this._hasMore = page * this._pageSize < response.total

            if (page === 1) {
                this._projectList = response.result || []
            } else {
                this._projectList = [...this._projectList, ...(response.result || [])]
            }

            if (this._projectList.length === 0) {
                this._suggestService.getHistoryItems()
            }
            return response
        }).finally(() => {
            if (isInitialLoad) {
                this._loading = false;
            } else {
                this._loadingMore = false;
            }
        })
    }

    // 加载更多数据
    async loadMore(): Promise<void> {
        if (this._loading || this._loadingMore || !this._hasMore) return;

        await this.getListWithPagination(this._currentPage + 1);
    }

    // 更新项目的成员数量
    updateProjectShareCount(projectId: string, increment = 1): void {
        const projectIndex = this._projectList.findIndex(p => p.work_project_id === projectId);
        if (projectIndex !== -1) {
            this._projectList[projectIndex].share_count = (this._projectList[projectIndex].share_count || 0) + increment;
        }
    }
}
